import React from 'react';

/**
 * Colonnes vidéo TrueView, partagées par les tableaux Google Ads.
 *
 * ## Pourquoi une fabrique plutôt que trois blocs recopiés
 *
 * Chaque tableau définit ses colonnes à la main (`label`, `sortKey`, `renderCell`,
 * `renderTotal`). Trois métriques sur une quinzaine de tableaux, c'est une soixantaine de
 * blocs quasi identiques : autant d'occasions de faire diverger une formule. Ici, une seule
 * définition, que chaque tableau étale dans sa configuration.
 *
 * ## Le taux de vue ne se dérive pas des impressions
 *
 * Google rapporte les vues aux impressions **vidéo**, pas aux impressions totales. Relevé sur
 * un compte réel : 42 vues, taux 0,256, pour 772 impressions au total — soit 164 impressions
 * vidéo. Un taux calculé sur les impressions totales aurait affiché 5,4 % au lieu de 25,6 %.
 *
 * Le backend fournit donc trois grandeurs **sommables** — `views`, `videoImpressions` et
 * `viewCost` — et les deux ratios s'en dérivent à n'importe quel niveau d'agrégation. Aucun
 * ratio n'est jamais moyenné.
 *
 * ## Un tiret plutôt qu'un zéro
 *
 * Sans vue, taux et CPV affichent `—` : sur un compte sans vidéo, un « 0,00 % » se lirait
 * comme un échec de diffusion là où il n'y a simplement rien à mesurer. Le nombre de vues,
 * lui, s'affiche bien à zéro — c'est un comptage, pas un ratio.
 *
 * @param {object} options
 * @param {Function} options.formatNumber formateur du tableau appelant
 * @param {Function} options.renderVariation rendu d'une variation (déjà lié à `compareEnabled`)
 * @param {Function} options.calculateVariation calcul d'une variation
 * @param {Function} [options.enveloppeValeur] habillage de la seule valeur — plusieurs tableaux
 *   la placent dans un `<div>` pour que la variation passe à la ligne.
 * @param {Function} [options.enveloppeCellule] habillage de la cellule entière — certains
 *   tableaux rendent la balise `<td>` eux-mêmes. Par défaut, aucun habillage.
 */
export function colonnesVideoGoogleAds({
  formatNumber,
  renderVariation,
  calculateVariation,
  enveloppeValeur = (valeur) => valeur,
  enveloppeCellule = (contenu) => contenu
}) {
  const tauxDeVue = (ligne) => ((ligne?.videoImpressions || 0) > 0
    ? ((ligne.views || 0) / ligne.videoImpressions) * 100
    : 0);

  const coutParVue = (ligne) => ((ligne?.views || 0) > 0
    ? (ligne.viewCost || 0) / ligne.views
    : 0);

  /**
   * Les tableaux n'attendent pas tous le même contrat : la plupart appellent `renderCell` et
   * `renderTotal`, mais celui des audiences réclame en plus `renderSummaryCell`, de même
   * signature, pour ses lignes de synthèse « avec / sans audience ». L'omettre lui faisait
   * lever « config.renderSummaryCell is not a function ». On le fournit donc systématiquement :
   * inoffensif pour les tableaux qui l'ignorent, indispensable pour celui qui l'attend.
   */
  const avecSynthese = (definition) => ({ ...definition, renderSummaryCell: definition.renderCell });

  return {
    views: avecSynthese({
      label: 'Vues',
      defaultLabel: 'Vues',
      sortKey: 'views',
      renderCell: (ligne, precedente, comparaison) => enveloppeCellule(
        <>
          {enveloppeValeur(formatNumber(ligne.views || 0))}
          {comparaison && renderVariation(calculateVariation(ligne.views || 0, precedente?.views || 0))}
        </>
      ),
      renderTotal: (totaux, totauxPrecedents) => enveloppeCellule(
        <>
          {enveloppeValeur(<strong>{formatNumber(totaux.views || 0)}</strong>)}
          {totauxPrecedents && renderVariation(calculateVariation(totaux.views || 0, totauxPrecedents.views || 0))}
        </>
      )
    }),

    vtr: avecSynthese({
      label: 'Taux de vue',
      defaultLabel: 'Taux de vue',
      sortKey: 'vtr',
      renderCell: (ligne, precedente, comparaison) => enveloppeCellule(
        <>
          {enveloppeValeur((ligne.views || 0) > 0 ? `${formatNumber(tauxDeVue(ligne), 2)}%` : '—')}
          {comparaison && (ligne.views || 0) > 0
            && renderVariation(calculateVariation(tauxDeVue(ligne), tauxDeVue(precedente)))}
        </>
      ),
      renderTotal: (totaux, totauxPrecedents) => enveloppeCellule(
        <>
          {enveloppeValeur(<strong>{(totaux.views || 0) > 0 ? `${formatNumber(tauxDeVue(totaux), 2)}%` : '—'}</strong>)}
          {totauxPrecedents && (totaux.views || 0) > 0
            && renderVariation(calculateVariation(tauxDeVue(totaux), tauxDeVue(totauxPrecedents)))}
        </>
      )
    }),

    cpv: avecSynthese({
      label: 'CPV',
      defaultLabel: 'CPV',
      sortKey: 'cpv',
      renderCell: (ligne, precedente, comparaison) => enveloppeCellule(
        <>
          {enveloppeValeur((ligne.views || 0) > 0 ? `${formatNumber(coutParVue(ligne), 3)}€` : '—')}
          {comparaison && (ligne.views || 0) > 0
            // Un CPV en baisse est une bonne nouvelle : variation inversée, comme le CPC.
            && renderVariation(calculateVariation(coutParVue(ligne), coutParVue(precedente)), true)}
        </>
      ),
      renderTotal: (totaux, totauxPrecedents) => enveloppeCellule(
        <>
          {enveloppeValeur(<strong>{(totaux.views || 0) > 0 ? `${formatNumber(coutParVue(totaux), 3)}€` : '—'}</strong>)}
          {totauxPrecedents && (totaux.views || 0) > 0
            && renderVariation(calculateVariation(coutParVue(totaux), coutParVue(totauxPrecedents)), true)}
        </>
      )
    })
  };
}

/**
 * Cumule les trois grandeurs vidéo d'une ligne dans un accumulateur de totaux.
 * À appeler dans le `reduce` d'un tableau, pour que `renderTotal` ait de quoi travailler.
 */
export function cumulerVideo(accumulateur, ligne) {
  return {
    views: (accumulateur.views || 0) + (ligne.views || 0),
    videoImpressions: (accumulateur.videoImpressions || 0) + (ligne.videoImpressions || 0),
    viewCost: (accumulateur.viewCost || 0) + (ligne.viewCost || 0)
  };
}

/** Valeurs de départ des trois grandeurs vidéo, pour l'état initial d'un `reduce`. */
export const TOTAUX_VIDEO_INITIAUX = { views: 0, videoImpressions: 0, viewCost: 0 };

/**
 * Variante pour les tableaux qui rendent eux-mêmes la balise `<td>` et nomment leur fonction
 * `render` plutôt que `renderCell`, la présence d'une ligne précédente valant comparaison.
 */
export function colonnesVideoEnCellules(options) {
  const base = colonnesVideoGoogleAds({
    ...options,
    enveloppeValeur: (valeur) => <div>{valeur}</div>,
    enveloppeCellule: (contenu) => (
      <td className="numeric" style={{ verticalAlign: 'top' }}>{contenu}</td>
    )
  });

  return Object.fromEntries(Object.entries(base).map(([cle, def]) => [cle, {
    sortKey: def.sortKey,
    defaultLabel: def.defaultLabel,
    render: (ligne, precedente) => def.renderCell(ligne, precedente, Boolean(precedente)),
    renderTotal: def.renderTotal
  }]));
}
